import { createSlice, PayloadAction } from '@reduxjs/toolkit';

import { AskLubavButtonEndpoint, College, ASK_LUBAV_BUTTON_ENDPOINTS } from '@backrs_ui/common-ui';

import { appStrings } from '@backrs_ui/grow-ui/src/assets/appStrings';

import { sendCollegeAskLubavPrompt, searchForCollegeById } from './college.thunks';
import { RootState } from '../store';

export type AskLubavUniversityBadge = Readonly<{
  id: string;
  endpoint: AskLubavButtonEndpoint | undefined;
  badgeName: string;
  title: string;
  response: string;
  isLoading?: boolean;
}>;

type CollegeState = Readonly<{
  college?: College;
  askLubavUniversityBadges: AskLubavUniversityBadge[];
  activeAskLubavUniversityBadge?: AskLubavUniversityBadge;
  loading?: boolean;
  error?: string;
}>;

const initialState: CollegeState = {
  college: undefined,
  loading: false,
  error: undefined,
  askLubavUniversityBadges: [
    {
      id: 'is-this-a-good-fit',
      endpoint: ASK_LUBAV_BUTTON_ENDPOINTS.COLLEGE_AM_I_A_GOOD_FIT,
      badgeName: 'Ask',
      title: 'Is this a good fit?',
      response: '',
      isLoading: false,
    },
    {
      id: 'example-extracurricular-profiles',
      endpoint: ASK_LUBAV_BUTTON_ENDPOINTS.COLLEGE_EXAMPLE_PROFILES,
      badgeName: 'Ask',
      title: 'Example extracurricular profiles',
      response: '',
      isLoading: false,
    },
    {
      id: 'what-do-students-say',
      endpoint: ASK_LUBAV_BUTTON_ENDPOINTS.COLLEGE_WHAT_DO_STUDENTS_SAY,
      badgeName: 'Ask',
      title: 'What do students say?',
      response: '',
      isLoading: false,
    },
    {
      id: 'campus-life',
      endpoint: ASK_LUBAV_BUTTON_ENDPOINTS.COLLEGE_CAMPUS_LIFE,
      badgeName: 'Ask',
      title: 'Campus life',
      response: '',
      isLoading: false,
    },
    {
      id: 'how-can-i-learn-more',
      endpoint: ASK_LUBAV_BUTTON_ENDPOINTS.COLLEGE_HOW_CAN_I_LEARN_MORE,
      badgeName: 'Ask',
      title: 'How can I learn more?',
      response: '',
      isLoading: false,
    },
    {
      id: 'improve-my-odds',
      endpoint: ASK_LUBAV_BUTTON_ENDPOINTS.COLLEGE_IMPROVE_MY_ODDS,
      badgeName: 'Pursue',
      title: 'Improve my odds',
      response: '',
      isLoading: false,
    },
  ],
};

export const collegeSlice = createSlice({
  name: 'collegeState',
  initialState,
  reducers: {
    resetCollege: (state) => {
      state.college = undefined;
      state.loading = false;
      state.error = undefined;
      state.askLubavUniversityBadges = initialState.askLubavUniversityBadges;
      state.activeAskLubavUniversityBadge = undefined;
    },
    setActiveAskLubavUniversityBadge: (
      state,
      action: PayloadAction<AskLubavUniversityBadge | undefined>,
    ) => {
      state.activeAskLubavUniversityBadge = action.payload;

      // Also update the corresponding badge in the askLubavBadges array
      if (action.payload) {
        state.askLubavUniversityBadges = state.askLubavUniversityBadges.map((badge) =>
          badge.id === action.payload?.id ? action.payload : badge,
        );
      }
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(searchForCollegeById.pending, (state) => {
        state.loading = true;
        state.error = undefined;
      })
      .addCase(searchForCollegeById.fulfilled, (state, action: PayloadAction<College>) => {
        state.college = action.payload;
        state.loading = false;
      })
      .addCase(searchForCollegeById.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      })

      .addCase(sendCollegeAskLubavPrompt.pending, (state) => {
        if (state.activeAskLubavUniversityBadge) {
          state.activeAskLubavUniversityBadge.isLoading = true;
        }
      })
      .addCase(sendCollegeAskLubavPrompt.fulfilled, (state, action: PayloadAction<string>) => {
        if (state.activeAskLubavUniversityBadge) {
          state.activeAskLubavUniversityBadge.isLoading = false;
          state.activeAskLubavUniversityBadge.response = action.payload;

          state.askLubavUniversityBadges = state.askLubavUniversityBadges.map((badge) =>
            badge.id === state.activeAskLubavUniversityBadge?.id
              ? state.activeAskLubavUniversityBadge
              : badge,
          );
        }
      })
      .addCase(sendCollegeAskLubavPrompt.rejected, (state) => {
        if (state.activeAskLubavUniversityBadge) {
          state.activeAskLubavUniversityBadge.isLoading = false;
          state.activeAskLubavUniversityBadge.response = appStrings.modules.chatBox.overloadErr;
        }
      });
  },
});

export const selectCollege = (state: RootState) => state.college.college;

export const { resetCollege, setActiveAskLubavUniversityBadge } = collegeSlice.actions;
